import { useCallback, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { Alert, Typography } from "@platform/design-system";

import CoverageNoteContent from "./CoverageNoteContent";
import RFPNoteContent from "./RfpNoteContent";

interface CarrierNotesPanelProps {
  ecoRfpId: string;
  onClose: VoidFunction;
  // Combined save fn (RFP + coverage) for the drawer to call when leaving the tab.
  registerFlush?: (flush: () => void) => void;
}

const RFP_NOTES_HEIGHT = 260;

/**
 * Combines the existing RFP note and coverage note editors into one panel. The
 * alert and RFP notes are fixed at the top; the coverage list (via AnchorNav)
 * owns the single scrollbar and fills the remaining height, so the panel never
 * nests a second scroll region. Each section keeps its own save behavior.
 */
const CarrierNotesPanel = ({
  ecoRfpId,
  onClose,
  registerFlush,
}: CarrierNotesPanelProps): React.JSX.Element => {
  const rfpFlushRef = useRef<() => void>(() => undefined);
  const coverageFlushRef = useRef<() => void>(() => undefined);
  const registerRfpFlush = useCallback((flush: () => void) => {
    rfpFlushRef.current = flush;
  }, []);
  const registerCoverageFlush = useCallback((flush: () => void) => {
    coverageFlushRef.current = flush;
  }, []);

  useEffect(() => {
    registerFlush?.(() => {
      rfpFlushRef.current();
      coverageFlushRef.current();
    });
  }, [registerFlush]);

  return (
    <Box
      role="tabpanel"
      id="tabpanel-carrier-notes"
      aria-labelledby="tab-carrier-notes"
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Alert severity="info">
        <strong>Carrier Notes:</strong> This information is shared externally.
        RFP and Coverage notes entered here will be sent to all carriers
        included in the RFP Submission and included in the Build tab export.
      </Alert>

      <Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
          RFP notes
        </Typography>
        <Box
          sx={{
            height: RFP_NOTES_HEIGHT,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <RFPNoteContent
            ecoRfpId={ecoRfpId}
            onClose={onClose}
            registerFlush={registerRfpFlush}
          />
        </Box>
      </Box>

      <Box
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
          Coverage notes
        </Typography>
        <CoverageNoteContent
          ecoRfpId={ecoRfpId}
          registerFlush={registerCoverageFlush}
        />
      </Box>
    </Box>
  );
};

export default CarrierNotesPanel;
